import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../components/pageProps/Breadcrumbs";
import ProductInfo from "../../components/pageProps/productDetails/ProductInfo";
import Slider from "react-slick";
import SampleNextArrow from "../../components/home/Men/SampleNextArrow";
import SamplePrevArrow from "../../components/home/Men/SamplePrevArrow";

const BASE_URL = process.env.REACT_APP_API_URL;

const productSlug = (name) => String(name).toLowerCase().split(" ").join("");

const mapApiProduct = (product) => ({
  _id: product.product_Id,
  img: `${BASE_URL}${product.product_images[0]?.image}`,
  images: product.product_images,
  productName: product.product_name,
  price: product.price,
  promo_percentage: product.promo_percentage,
  discounted_price: product.discounted_price,
  colors: product.colors,
  badge: product.badge,
  des: product.description,
  gender: product.gender,
  season: product.season,
});

const ProductDetails = () => {
  const location = useLocation();
  const { _id } = useParams();
  const { t } = useTranslation();
  const [prevLocation, setPrevLocation] = useState("");
  const [productInfo, setProductInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPrevLocation(location.pathname);

    if (location.state?.item) {
      setProductInfo(location.state.item);
      setNotFound(false);
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        if (/^\d+$/.test(_id)) {
          const response = await axios.get(`${BASE_URL}/api/products/${_id}/`);
          setProductInfo(mapApiProduct(response.data));
          return;
        }

        const response = await axios.get(`${BASE_URL}/api/products/`);
        const found = response.data.find(
          (product) => productSlug(product.product_name) === _id
        );

        if (found) {
          setProductInfo(mapApiProduct(found));
        } else {
          setProductInfo(null);
          setNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        setProductInfo(null);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [location, _id]);

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
  };

  if (loading) {
    return (
      <div className="w-full mx-auto border-b-[1px] border-b-gray-300">
        <div className="max-w-container mx-auto px-4 py-20 flex justify-center items-center">
          <span className="dot-animation">• • •</span>
          <style>{`
            .dot-animation {
              font-size: 2rem;
              animation: blink 1.5s infinite;
            }
            @keyframes blink {
              0%, 100% { opacity: 0.2; }
              50% { opacity: 1; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (notFound || !productInfo) {
    return (
      <div className="w-full mx-auto border-b-[1px] border-b-gray-300">
        <div className="max-w-container mx-auto px-4 py-20 flex flex-col items-center gap-6">
          <p className="font-poppins text-lg text-gray-600">{t("productf")}</p>
          <Link
            to="/shop"
            className="bg-primeColor hover:bg-black duration-300 text-white px-8 py-3 font-titleFont"
          >
            {t("Continue")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto border-b-[1px] border-b-gray-300">
      <div className="max-w-container mx-auto px-4">
        <div className="xl:-mt-10 -mt-7">
          <Breadcrumbs title="" prevLocation={prevLocation} />
        </div>
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-6 gap-4 h-full -mt-5 xl:-mt-8 pb-10 bg-gray-100 p-4">
          <div className="h-full md:col-span-2 xl:col-span-2">
            <Slider {...settings}>
              {productInfo.images &&
                productInfo.images.map((image, index) => (
                  <div key={index} className="h-full">
                    <img
                      className="w-full h-full object-cover"
                      src={`${BASE_URL}${image.image}`}
                      alt={`Product ${index + 1}`}
                    />
                  </div>
                ))}
            </Slider>
          </div>
          <div className="h-full w-full md:col-span-2 xl:col-span-3 xl:p-14 flex flex-col gap-6 justify-center">
            <ProductInfo productInfo={productInfo} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;

